import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Html5Qrcode } from 'html5-qrcode';
import {
  Camera, ArrowRight, ArrowLeft, Check, Loader2, AlertCircle,
  ScanLine, MapPin, RefreshCw, Upload, Settings, Smartphone, CheckCircle, Search
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { extractShortCode } from '@/utils/medallionUtils';
import { buildMemorialDisplayName } from "@/utils/memorialDisplayName";
import { usePortalAuth } from '@/hooks/usePortalAuth';
import { useReadOnly } from '@/contexts/ReadOnlyContext';
import { useAuth } from '@/contexts/AuthContext';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { compressImage } from '@/utils/imageCompression';
import { captureException } from '@/lib/sentry';
import { toast } from 'sonner';
import { FunctionsHttpError } from '@supabase/supabase-js';

type Step = 'pre-check' | 'scan' | 'select' | 'confirm' | 'success';
type PermState = 'unknown' | 'granted' | 'denied' | 'prompt' | 'unsupported';
type CameraBlockSource = 'policy' | 'os' | 'browser' | 'unknown';

interface UninstalledMemorial {
  id: string;
  first_name: string;
  last_name: string | null;
  intended_cemetery_name: string | null;
  intended_city: string | null;
  client_email: string | null;
  contact_phone: string | null;
  created_at: string;
}

interface GeolocationData {
  latitude: number;
  longitude: number;
  accuracy: number;
}

// GPS accuracy thresholds
const ACCURACY_THRESHOLD_GOOD = 20; // meters — acceptable accuracy. 20m is
// still tight enough to identify a grave (covers ~3-6 graves max in a row)
// while being achievable under tree canopy / urban cemeteries / cloudy days.
const GPS_WAIT_TIMEOUT_MS = 30000; // 30 seconds - then accept whatever accuracy we have
const MAX_AUTO_RETRIES = 1; // silently retry once on transient GPS failures
// before showing an error to the user.

const PortalUploadPhoto = () => {
  const navigate = useNavigate();
  const { company } = usePortalAuth();
  const { isReadOnly } = useReadOnly();
  const { user } = useAuth();

  // Step state
  const [step, setStep] = useState<Step>('pre-check');

  // Pre-check permission state
  const [cameraPerm, setCameraPerm] = useState<PermState>('unknown');
  const [locationPerm, setLocationPerm] = useState<PermState>('unknown');
  const [showCameraHelp, setShowCameraHelp] = useState(false);
  const [showLocationHelp, setShowLocationHelp] = useState(false);
  const [isRequestingCamera, setIsRequestingCamera] = useState(false);
  const [isRequestingLocation, setIsRequestingLocation] = useState(false);
  const [cameraBlockSource, setCameraBlockSource] = useState<CameraBlockSource>('unknown');

  // Medallion state (Step 1)
  const [medallionShortCode, setMedallionShortCode] = useState<string | null>(null);

  // Memorial selection state (Step 2)
  const [memorials, setMemorials] = useState<UninstalledMemorial[]>([]);
  const [memorialSearch, setMemorialSearch] = useState('');
  const [selectedMemorial, setSelectedMemorial] = useState<UninstalledMemorial | null>(null);
  const [isLoadingMemorials, setIsLoadingMemorials] = useState(false);

  // Scanning state
  const [isScanning, setIsScanning] = useState(false);
  const [manualCode, setManualCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const scannerRef = useRef<Html5Qrcode | null>(null);

  // Native BarcodeDetector path — used when the browser exposes the OS-level
  // scanner (Chrome on Android/Desktop, Safari iOS 17.4+). Dramatically faster
  // and more accurate than the JS-only html5-qrcode fallback. scannerKind is
  // state (not a ref) because the JSX conditionally renders either a <video>
  // or the html5-qrcode container based on which path is active.
  const [scannerKind, setScannerKind] = useState<'native' | 'fallback' | null>(null);
  const nativeVideoRef = useRef<HTMLVideoElement | null>(null);
  const nativeStreamRef = useRef<MediaStream | null>(null);
  const nativeRafIdRef = useRef<number | null>(null);
  const nativeDetectorRef = useRef<{ detect: (source: HTMLVideoElement) => Promise<Array<{ rawValue: string }>> } | null>(null);

  // Camera and capture state
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [isCameraActive, setIsCameraActive] = useState(false);
  const [capturedPhoto, setCapturedPhoto] = useState<string | null>(null);
  const [capturedBlob, setCapturedBlob] = useState<Blob | null>(null);
  const [geolocation, setGeolocation] = useState<GeolocationData | null>(null);
  const [geoError, setGeoError] = useState<string | null>(null);
  const [isGettingLocation, setIsGettingLocation] = useState(false);

  // Continuous GPS tracking state
  const [bestLocation, setBestLocation] = useState<GeolocationData | null>(null);
  const [isWatchingLocation, setIsWatchingLocation] = useState(false);
  const locationWatchIdRef = useRef<number | null>(null);
  // Auto-retry tracking for transient GPS errors. retryCount caps how many
  // times we silently restart the watch before surfacing an error; the
  // timeout ref lets stopLocationWatch cancel a pending retry mid-flight.
  const retryCountRef = useRef(0);
  const retryTimeoutRef = useRef<number | null>(null);
  const [locationWatchStartTime, setLocationWatchStartTime] = useState<number | null>(null);
  const [showAccuracyOverride, setShowAccuracyOverride] = useState(false);

  // Location permission dialog state
  const [showLocationDialog, setShowLocationDialog] = useState(false);
  const [locationPermissionDenied, setLocationPermissionDenied] = useState(false);

  // =========================================================================
  // GPS Functions (reused from previous Upload Photo)
  // =========================================================================

  const stopLocationWatch = useCallback(() => {
    if (locationWatchIdRef.current !== null) {
      navigator.geolocation.clearWatch(locationWatchIdRef.current);
      locationWatchIdRef.current = null;
    }
    if (retryTimeoutRef.current !== null) {
      window.clearTimeout(retryTimeoutRef.current);
      retryTimeoutRef.current = null;
    }
    setIsWatchingLocation(false);
    setLocationWatchStartTime(null);
  }, []);

  const startLocationWatch = useCallback((isRetry = false) => {
    if (!isRetry) {
      // Fresh attempt (user-triggered or new camera session) — reset the
      // silent-retry budget. Auto-retries pass isRetry=true to preserve it.
      retryCountRef.current = 0;
    }
    setIsWatchingLocation(true);
    setIsGettingLocation(true);
    setGeoError(null);
    setLocationPermissionDenied(false);
    setBestLocation(null);
    setGeolocation(null);
    setShowAccuracyOverride(false);
    setLocationWatchStartTime(Date.now());

    const watchId = navigator.geolocation.watchPosition(
      (position) => {
        const newLocation = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy,
        };
        setBestLocation(prev => {
          if (!prev || newLocation.accuracy < prev.accuracy) {
            setGeolocation(newLocation);
            return newLocation;
          }
          return prev;
        });
        setIsGettingLocation(false);
        // Successful read — give the next failure a fresh retry budget.
        retryCountRef.current = 0;
      },
      (err) => {
        console.error('Geolocation error:', err);
        setIsGettingLocation(false);
        switch (err.code) {
          case err.PERMISSION_DENIED:
            setLocationPermissionDenied(true);
            setGeoError('Location access denied');
            setShowLocationDialog(true);
            stopLocationWatch();
            break;
          case err.POSITION_UNAVAILABLE:
          case err.TIMEOUT:
            // Many transient signal blips (entering/leaving tree cover, brief
            // sky obstruction) clear within seconds. Silently restart the
            // watch up to MAX_AUTO_RETRIES before showing the user an error.
            if (retryCountRef.current < MAX_AUTO_RETRIES) {
              retryCountRef.current++;
              stopLocationWatch();
              retryTimeoutRef.current = window.setTimeout(() => {
                retryTimeoutRef.current = null;
                startLocationWatch(true);
              }, 1000);
            } else {
              setGeoError(err.code === err.TIMEOUT
                ? 'Location request timed out. Please try again.'
                : 'Location unavailable. Please try again.');
            }
            break;
          default:
            setGeoError('Unable to get location. Please try again.');
        }
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
    );
    locationWatchIdRef.current = watchId;
  }, [stopLocationWatch]);

  const acceptCurrentAccuracy = useCallback(() => {
    if (bestLocation) {
      setGeolocation(bestLocation);
    }
    stopLocationWatch();
    setShowAccuracyOverride(false);
  }, [bestLocation, stopLocationWatch]);

  // Auto-accept GPS after 30 seconds timeout
  useEffect(() => {
    if (isWatchingLocation && locationWatchStartTime && bestLocation) {
      const elapsed = Date.now() - locationWatchStartTime;
      const remaining = GPS_WAIT_TIMEOUT_MS - elapsed;

      if (remaining <= 0) {
        setGeolocation(bestLocation);
        stopLocationWatch();
        setShowAccuracyOverride(false);
      } else {
        const timer = setTimeout(() => {
          if (bestLocation) {
            setGeolocation(bestLocation);
            stopLocationWatch();
            setShowAccuracyOverride(false);
          }
        }, remaining);
        return () => clearTimeout(timer);
      }
    }
  }, [isWatchingLocation, locationWatchStartTime, bestLocation, stopLocationWatch]);

  // Connect stream to video element when both are ready
  useEffect(() => {
    if (isCameraActive && videoRef.current && streamRef.current) {
      videoRef.current.srcObject = streamRef.current;
    }
  }, [isCameraActive]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopScanner();
      stopCamera();
      stopLocationWatch();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stopLocationWatch]);

  // Lift the floating AIChatBubble out of the way of the sticky bottom action
  // bar that appears once a memorial is selected. The bubble reads the
  // --chat-bottom CSS variable (defaults to 1.5rem when unset).
  useEffect(() => {
    if (step !== 'select' || !selectedMemorial) return;
    document.documentElement.style.setProperty('--chat-bottom', '89px');
    return () => document.documentElement.style.removeProperty('--chat-bottom');
  }, [step, selectedMemorial]);

  // =========================================================================
  // QR Scanner Functions
  // =========================================================================

  // Construct a native BarcodeDetector if the browser supports it. Returns
  // null if unavailable or if construction throws (e.g. format unsupported),
  // which signals the caller to use the html5-qrcode fallback.
  const createNativeDetector = (): { detect: (source: HTMLVideoElement) => Promise<Array<{ rawValue: string }>> } | null => {
    try {
      const Ctor = (window as { BarcodeDetector?: new (opts: { formats: string[] }) => { detect: (source: HTMLVideoElement) => Promise<Array<{ rawValue: string }>> } }).BarcodeDetector;
      if (!Ctor) return null;
      return new Ctor({ formats: ['qr_code'] });
    } catch {
      return null;
    }
  };

  // Native scanner: opens the camera directly, attaches the stream to a
  // <video> we render in the scan step, then runs a requestAnimationFrame
  // loop calling detector.detect(video) until a QR code is decoded. On
  // success we hand off to the same handleQrCodeScanned the fallback uses.
  const startNativeScanner = async (detector: NonNullable<ReturnType<typeof createNativeDetector>>) => {
    const stream = await navigator.mediaDevices.getUserMedia({
      video: {
        facingMode: 'environment',
        // Request 4K so QRs captured from a foot or more away still have
        // enough pixels for the decoder. Browsers fall back to whatever the
        // device can actually deliver, so phones that cap at 1080p just give
        // us 1080p — there's no harm in asking for more.
        width: { ideal: 3840 },
        height: { ideal: 2160 },
      },
    });
    nativeStreamRef.current = stream;
    nativeDetectorRef.current = detector;

    const video = nativeVideoRef.current;
    if (!video) {
      stream.getTracks().forEach(t => t.stop());
      throw new Error('Native scanner video element missing');
    }
    video.srcObject = stream;
    await video.play();

    let scanned = false;
    const tick = async () => {
      if (scanned || !nativeDetectorRef.current || !nativeStreamRef.current) return;
      try {
        const results = await nativeDetectorRef.current.detect(video);
        if (results && results.length > 0) {
          const value = results[0].rawValue;
          if (value) {
            scanned = true;
            handleQrCodeScanned(value);
            return;
          }
        }
      } catch {
        // Ignore per-frame detection failures; keep polling.
      }
      nativeRafIdRef.current = requestAnimationFrame(tick);
    };
    nativeRafIdRef.current = requestAnimationFrame(tick);
  };

  const stopNativeScanner = () => {
    if (nativeRafIdRef.current !== null) {
      cancelAnimationFrame(nativeRafIdRef.current);
      nativeRafIdRef.current = null;
    }
    if (nativeStreamRef.current) {
      nativeStreamRef.current.getTracks().forEach(t => t.stop());
      nativeStreamRef.current = null;
    }
    if (nativeVideoRef.current) {
      nativeVideoRef.current.srcObject = null;
    }
    nativeDetectorRef.current = null;
  };

  const startFallbackScanner = async () => {
    const html5QrCode = new Html5Qrcode('qr-reader-install');
    scannerRef.current = html5QrCode;
    await html5QrCode.start(
      { facingMode: 'environment' },
      {
        fps: 25,
        // qrbox/aspectRatio left unset so the whole high-res frame is scanned.
        videoConstraints: {
          facingMode: 'environment',
          // 4K when available so QRs from a foot+ away still resolve. The
          // device downsamples for us if it can't deliver this resolution.
          width: { ideal: 3840 },
          height: { ideal: 2160 },
          // focusMode isn't in the standard MediaTrackConstraints type yet but
          // most modern mobile browsers honor it.
          focusMode: 'continuous',
        } as MediaTrackConstraints,
      },
      (decodedText) => handleQrCodeScanned(decodedText),
      () => { /* ignore per-frame scan failures */ }
    );
  };

  const startScanner = async () => {
    setError(null);
    setIsScanning(true);

    const nativeDetector = createNativeDetector();
    const kind: 'native' | 'fallback' = nativeDetector ? 'native' : 'fallback';
    setScannerKind(kind);

    // Wait for React to mount the right element (the <video> for native, the
    // qr-reader-install div for fallback) before we hand a ref to the scanner.
    await new Promise(resolve => setTimeout(resolve, 100));

    try {
      if (nativeDetector) {
        await startNativeScanner(nativeDetector);
      } else {
        await startFallbackScanner();
      }
    } catch (err) {
      console.error('Error starting scanner:', err);
      // If native path failed for any reason, try the JS fallback before
      // surfacing an error — both paths depend on getUserMedia, but the
      // fallback library has slightly different camera-init quirks and
      // sometimes succeeds where the native path failed.
      if (kind === 'native') {
        stopNativeScanner();
        setScannerKind('fallback');
        await new Promise(resolve => setTimeout(resolve, 100));
        try {
          await startFallbackScanner();
          return;
        } catch (fallbackErr) {
          console.error('Fallback scanner also failed:', fallbackErr);
        }
      }
      setScannerKind(null);
      setIsScanning(false);
      setError('Unable to access camera. Please check permissions or enter the code manually.');
    }
  };

  const stopScanner = async () => {
    if (scannerKind === 'native') {
      stopNativeScanner();
    } else if (scannerRef.current) {
      try {
        if (scannerRef.current.isScanning) {
          await scannerRef.current.stop();
        }
        scannerRef.current.clear();
      } catch (err) {
        console.error('Error stopping scanner:', err);
      }
      scannerRef.current = null;
    }
    setScannerKind(null);
    setIsScanning(false);
  };

  const handleQrCodeScanned = async (input: string) => {
    await stopScanner();
    await processMedallionScan(input);
  };

  // =========================================================================
  // Camera Functions
  // =========================================================================

  const checkLocationPermission = async (): Promise<'granted' | 'denied' | 'prompt'> => {
    if (!navigator.permissions) return 'prompt';
    try {
      const result = await navigator.permissions.query({ name: 'geolocation' });
      return result.state;
    } catch {
      return 'prompt';
    }
  };

  // =========================================================================
  // Pre-check permission helpers
  // =========================================================================

  const isSecureContextOk = (): boolean =>
    typeof window === 'undefined' ? true : window.isSecureContext;

  // Passive camera permission probe. Safari throws when querying 'camera' via
  // the Permissions API, so we fall back to 'prompt' rather than attempting an
  // active getUserMedia probe — that would auto-trigger the OS permission dialog.
  // When the API reports 'granted', we still verify with a brief getUserMedia
  // call: Chrome's Permissions API only reflects site-level permission and is
  // blind to OS-level revocation (e.g. macOS Privacy & Security → Camera off
  // for Chrome), which would otherwise let users through the gate only to fail
  // with "NotAllowedError: Permission denied by system" at scan time.
  const checkCameraPermissionState = async (): Promise<PermState> => {
    if (!navigator.mediaDevices?.getUserMedia) return 'unsupported';
    if (!navigator.permissions) return 'prompt';
    let state: PermState;
    try {
      const result = await navigator.permissions.query({ name: 'camera' as PermissionName });
      state = result.state as PermState;
    } catch {
      state = 'prompt';
    }
    if (state === 'granted') {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } });
        stream.getTracks().forEach(t => t.stop());
        return 'granted';
      } catch {
        return 'denied';
      }
    }
    return state;
  };

  // Same probe-verify pattern for location: the Permissions API can report
  // 'granted' even when the OS has revoked Location Services for the browser,
  // so we confirm with a quick getCurrentPosition call. maximumAge:60000 lets
  // the browser return a cached fix immediately when one exists, keeping the
  // pre-check fast.
  const checkLocationPermissionState = async (): Promise<PermState> => {
    if (!('geolocation' in navigator)) return 'unsupported';
    const passive = await checkLocationPermission();
    if (passive === 'granted') {
      return new Promise<PermState>((resolve) => {
        navigator.geolocation.getCurrentPosition(
          () => resolve('granted'),
          (err) => {
            if (err.code === err.PERMISSION_DENIED) resolve('denied');
            // Timeout / position unavailable isn't a permission failure —
            // don't lock the user out for a slow GPS or transient signal loss.
            else resolve('granted');
          },
          { enableHighAccuracy: false, timeout: 5000, maximumAge: 60000 }
        );
      });
    }
    return passive as PermState;
  };

  const requestCameraAccess = async (): Promise<PermState> => {
    if (!navigator.mediaDevices?.getUserMedia) return 'unsupported';
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' },
      });
      stream.getTracks().forEach(track => track.stop());
      return 'granted';
    } catch (err) {
      const name = (err as { name?: string })?.name;
      if (name === 'NotAllowedError' || name === 'SecurityError') return 'denied';
      if (name === 'NotFoundError' || name === 'OverconstrainedError') return 'unsupported';
      return 'denied';
    }
  };

  const requestLocationAccess = async (): Promise<PermState> => {
    if (!('geolocation' in navigator)) return 'unsupported';
    return new Promise<PermState>((resolve) => {
      navigator.geolocation.getCurrentPosition(
        () => resolve('granted'),
        (err) => {
          if (err.code === err.PERMISSION_DENIED) resolve('denied');
          else resolve('prompt');
        },
        { enableHighAccuracy: false, timeout: 10000, maximumAge: 60000 }
      );
    });
  };

  // When camera is denied, figure out *which* layer is blocking so we can
  // surface the right recovery instructions:
  //   - 'policy'  → Permissions-Policy HTTP header forbids camera (server-side)
  //   - 'os'      → OS has revoked the browser's camera access (e.g. macOS
  //                 Privacy & Security → Camera off for Chrome). Chrome tells
  //                 us this directly via "Permission denied by system" in the
  //                 getUserMedia error. enumerateDevices returning zero video
  //                 inputs is a secondary fallback for non-Chrome browsers.
  //   - 'browser' → camera reachable but not allowed for this site
  //   - 'unknown' → API unavailable or signal is ambiguous
  const detectCameraBlockSource = async (): Promise<CameraBlockSource> => {
    try {
      const fp = (document as { featurePolicy?: { allowsFeature?: (name: string) => boolean } }).featurePolicy;
      if (fp?.allowsFeature && !fp.allowsFeature('camera')) return 'policy';
    } catch { /* ignore */ }

    if (!navigator.mediaDevices?.getUserMedia) return 'unknown';

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } });
      stream.getTracks().forEach(t => t.stop());
      // Probe succeeded unexpectedly — caller thought we were denied. Treat
      // as ambiguous; the polling effect will re-evaluate state next tick.
      return 'unknown';
    } catch (err) {
      const message = ((err as { message?: string })?.message || '').toLowerCase();
      // Chrome appends "by system" specifically when the OS has blocked the
      // browser. Most reliable signal for OS-level revocation.
      if (message.includes('by system') || message.includes('system permission')) {
        return 'os';
      }
      // Fallback heuristic for browsers that don't include the "by system"
      // suffix: when OS blocks camera entirely, enumerateDevices often returns
      // no video inputs at all because the browser can't see the hardware.
      try {
        if (navigator.mediaDevices?.enumerateDevices) {
          const devices = await navigator.mediaDevices.enumerateDevices();
          const videoInputs = devices.filter(d => d.kind === 'videoinput');
          if (videoInputs.length === 0) return 'os';
        }
      } catch { /* ignore */ }
      return 'browser';
    }
  };

  const handleAllowCamera = async () => {
    setIsRequestingCamera(true);
    setShowCameraHelp(false);
    const state = await requestCameraAccess();
    setCameraPerm(state);
    if (state === 'denied') {
      setCameraBlockSource(await detectCameraBlockSource());
      setShowCameraHelp(true);
    }
    setIsRequestingCamera(false);
  };

  const handleAllowLocation = async () => {
    setIsRequestingLocation(true);
    setShowLocationHelp(false);
    const state = await requestLocationAccess();
    setLocationPerm(state);
    if (state === 'denied') setShowLocationHelp(true);
    setIsRequestingLocation(false);
  };

  // Poll permission state on mount and when the tab regains visibility (so
  // status flips automatically after the installer fixes things in Settings).
  useEffect(() => {
    if (step !== 'pre-check') return;
    let cancelled = false;
    const poll = async () => {
      const [cam, loc] = await Promise.all([
        checkCameraPermissionState(),
        checkLocationPermissionState(),
      ]);
      if (cancelled) return;
      setCameraPerm(cam);
      setLocationPerm(loc);
      if (cam === 'granted') setShowCameraHelp(false);
      if (loc === 'granted') setShowLocationHelp(false);
      // Auto-reveal recovery instructions when blocked is detected on load —
      // installers shouldn't have to discover the "Show me how to fix this"
      // button before they see any guidance.
      if (cam === 'denied') setShowCameraHelp(true);
      if (loc === 'denied') setShowLocationHelp(true);
      if (cam === 'denied') {
        const src = await detectCameraBlockSource();
        if (!cancelled) setCameraBlockSource(src);
      }
    };
    poll();
    const onVis = () => {
      if (document.visibilityState === 'visible') poll();
    };
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('focus', onVis);
    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('focus', onVis);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  const startCamera = async () => {
    setError(null);
    setGeolocation(null);
    setGeoError(null);
    setLocationPermissionDenied(false);

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment', width: { ideal: 1920 }, height: { ideal: 1080 } }
      });
      streamRef.current = stream;
      setIsCameraActive(true);

      const permissionStatus = await checkLocationPermission();
      if (permissionStatus === 'denied') {
        setLocationPermissionDenied(true);
        setShowLocationDialog(true);
      } else {
        startLocationWatch();
      }
    } catch (err) {
      console.error('Error accessing camera:', err);
      setError('Unable to access camera. Please check permissions.');
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    setIsCameraActive(false);
  };

  // track.stop() returns immediately but doesn't guarantee the OS has
  // actually released the physical camera. Requesting a new getUserMedia()
  // stream too soon after stopping the QR scanner's stream can silently
  // succeed with a black/frozen feed instead of erroring — worse odds since
  // the scanner started requesting 4K (see 7c5a9588). Give the hardware a
  // moment to let go before opening the photo camera.
  const CAMERA_HANDOFF_DELAY_MS = 400;

  const handleContinueToPhoto = async () => {
    setStep('confirm');
    await stopScanner();
    await new Promise((resolve) => setTimeout(resolve, CAMERA_HANDOFF_DELAY_MS));
    startCamera();
  };

  const handleRetryLocation = () => {
    setShowLocationDialog(false);
    startLocationWatch();
  };

  const capturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.drawImage(video, 0, 0);
    const dataUrl = canvas.toDataURL('image/jpeg', 0.9);
    setCapturedPhoto(dataUrl);

    canvas.toBlob((blob) => {
      if (blob) setCapturedBlob(blob);
    }, 'image/jpeg', 0.9);

    // Freeze GPS at best reading
    if (bestLocation) {
      setGeolocation(bestLocation);
    }
    stopLocationWatch();
    stopCamera();
  };

  const retakePhoto = () => {
    setCapturedPhoto(null);
    setCapturedBlob(null);
    setBestLocation(null);
    setShowAccuracyOverride(false);
    startCamera();
  };

  // =========================================================================
  // Medallion Scan Processing (Step 1)
  // =========================================================================

  const processMedallionScan = async (input: string) => {
    setError(null);
    setIsLoading(true);

    try {
      const shortCode = extractShortCode(input);
      if (!shortCode) {
        throw new Error('Invalid medallion code. Please scan a valid Pontis QR code or enter the 6-character code.');
      }

      // Look up medallion
      const { data: medallion, error: medallionError } = await supabase
        .from('medallions')
        .select('id, short_code, status, memorial_id, memorials(first_name, last_name)')
        .eq('short_code', shortCode)
        .single();

      if (medallionError || !medallion) {
        throw new Error('Invalid medallion code. This code was not found in our system.');
      }

      if (medallion.status === 'linked') {
        const linked = medallion.memorials as { first_name: string; last_name: string | null } | null;
        const linkedName = linked
          ? buildMemorialDisplayName(linked)
          : 'a memorial';
        throw new Error(`This medallion is already installed for ${linkedName}. Scan a different one.`);
      }

      // status === 'generated' — proceed
      setMedallionShortCode(shortCode);
      setStep('select');
      await fetchUninstalledMemorials();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to process medallion code');
    } finally {
      setIsLoading(false);
    }
  };

  const handleManualCodeSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!manualCode.trim()) return;
    await processMedallionScan(manualCode.trim());
  };

  // =========================================================================
  // Memorial Selection (Step 2)
  // =========================================================================

  const fetchUninstalledMemorials = async () => {
    if (!company?.id) return;

    setIsLoadingMemorials(true);
    try {
      const { data, error: fetchError } = await supabase
        .from('memorials')
        .select('id, first_name, last_name, intended_cemetery_name, intended_city, client_email, contact_phone, created_at')
        .eq('monument_company_id', company.id)
        .eq('is_installed', false)
        .order('created_at', { ascending: false });

      if (fetchError) throw fetchError;
      setMemorials(data || []);
    } catch (err) {
      console.error('Error fetching memorials:', err);
      toast.error('Failed to load memorials');
    } finally {
      setIsLoadingMemorials(false);
    }
  };

  // =========================================================================
  // Installation (Step 3 submit)
  // =========================================================================

  const handleInstallation = async () => {
    if (!selectedMemorial || !company || !medallionShortCode || !geolocation || !capturedBlob) return;

    setIsLoading(true);
    setError(null);

    try {
      // 1. Compress photo
      const file = new File([capturedBlob], `installation-${selectedMemorial.id}.jpg`, { type: 'image/jpeg' });
      const { compressedFile, thumbnail } = await compressImage(file);

      // 2. Upload main photo to storage
      const photoPath = `${company.id}/${selectedMemorial.id}.webp`;
      const { error: uploadError } = await supabase.storage
        .from('monument-photos')
        .upload(photoPath, compressedFile, { upsert: true });
      if (uploadError) throw uploadError;

      // 3. Upload thumbnail
      const thumbPath = `${company.id}/${selectedMemorial.id}-thumb.webp`;
      await supabase.storage
        .from('monument-photos')
        .upload(thumbPath, thumbnail, { upsert: true });

      // 4. Get public URLs
      const { data: { publicUrl: photoUrl } } = supabase.storage
        .from('monument-photos')
        .getPublicUrl(photoPath);
      const { data: { publicUrl: thumbUrl } } = supabase.storage
        .from('monument-photos')
        .getPublicUrl(thumbPath);

      // 5. Upsert monument_installations record (unique on memorial_id)
      const { error: insertError } = await supabase
        .from('monument_installations')
        .upsert({
          memorial_id: selectedMemorial.id,
          company_id: company.id,
          uploaded_by: user?.id || null,
          photo_url: photoUrl,
          thumbnail_url: thumbUrl,
          latitude: geolocation.latitude,
          longitude: geolocation.longitude,
          location_accuracy: geolocation.accuracy,
          captured_at: new Date().toISOString(),
        }, { onConflict: 'memorial_id' });
      if (insertError) throw insertError;

      // 6. Call link-medallion edge function
      const { data: linkResult, error: linkError } = await supabase.functions.invoke(
        'link-medallion',
        {
          body: {
            shortCode: medallionShortCode,
            memorialId: selectedMemorial.id,
            latitude: geolocation.latitude,
            longitude: geolocation.longitude,
            photoUrl,
          },
        }
      );

      if (linkError) {
        let message = 'Failed to link medallion';
        if (linkError instanceof FunctionsHttpError && linkError.context?.json) {
          try { const body = await linkError.context.json(); if (body?.error) message = body.error; } catch { /* ignore parse error */ }
        }
        throw new Error(message);
      }

      if (linkResult && !linkResult.success) {
        throw new Error(linkResult.error || 'Failed to link medallion');
      }

      setStep('success');
    } catch (err: unknown) {
      console.error('Installation error:', err);
      if (err instanceof Error) {
        captureException(err, { memorialId: selectedMemorial.id, medallionShortCode, context: 'install-medallion' });
      }
      setError(err instanceof Error ? err.message : 'Failed to complete installation. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // =========================================================================
  // Reset
  // =========================================================================

  const handleReset = () => {
    setStep('pre-check');
    setMedallionShortCode(null);
    setSelectedMemorial(null);
    setMemorials([]);
    setMemorialSearch('');
    setManualCode('');
    setError(null);
    setCapturedPhoto(null);
    setCapturedBlob(null);
    setGeolocation(null);
    setBestLocation(null);
    setGeoError(null);
    setLocationPermissionDenied(false);
    setShowAccuracyOverride(false);
    setShowCameraHelp(false);
    setShowLocationHelp(false);
    stopCamera();
    stopLocationWatch();
  };

  // =========================================================================
  // Render: Pre-check — Permissions Gate
  // =========================================================================

  // Browser-level recovery (lock icon / site settings — fixable without leaving the browser).
  const renderBrowserSteps = (kind: 'Camera' | 'Location') => (
    <div className="space-y-2">
      <div className="flex items-start gap-3 p-3 bg-muted/50 rounded-lg">
        <Smartphone className="h-5 w-5 text-muted-foreground mt-0.5 shrink-0" />
        <div>
          <p className="font-medium text-sm">iPhone &middot; Safari</p>
          <ol className="text-xs text-muted-foreground space-y-1 mt-1">
            <li>1. Tap the <span className="font-medium">&ldquo;AA&rdquo;</span> button on the left of the address bar</li>
            <li>2. Tap <span className="font-medium">Website Settings</span></li>
            <li>3. Set <span className="font-medium">{kind}</span> to <span className="font-medium">Allow</span></li>
            <li>4. Return here and tap &ldquo;Try Again&rdquo;</li>
          </ol>
        </div>
      </div>
      <div className="flex items-start gap-3 p-3 bg-muted/50 rounded-lg">
        <Settings className="h-5 w-5 text-muted-foreground mt-0.5 shrink-0" />
        <div>
          <p className="font-medium text-sm">Android &middot; Chrome</p>
          <ol className="text-xs text-muted-foreground space-y-1 mt-1">
            <li>1. Tap the <span className="font-medium">lock icon</span> in the address bar</li>
            <li>2. Tap <span className="font-medium">Permissions</span> {'→'} <span className="font-medium">{kind}</span></li>
            <li>3. Select <span className="font-medium">Allow</span></li>
            <li>4. Return here and tap &ldquo;Try Again&rdquo;</li>
          </ol>
        </div>
      </div>
    </div>
  );

  // OS / system-level recovery (phone or laptop Settings app — needed when the
  // OS itself has revoked the browser's access to the camera/location hardware).
  const renderOsSteps = (kind: 'Camera' | 'Location') => {
    const iosSection = kind === 'Camera' ? (
      <ol className="text-xs text-muted-foreground space-y-1 mt-1">
        <li>1. Open <span className="font-medium">Settings</span> {'→'} <span className="font-medium">Safari</span></li>
        <li>2. Tap <span className="font-medium">Camera</span></li>
        <li>3. Set to <span className="font-medium">Allow</span> or <span className="font-medium">Ask</span></li>
      </ol>
    ) : (
      <ol className="text-xs text-muted-foreground space-y-1 mt-1">
        <li>1. Open <span className="font-medium">Settings</span> {'→'} <span className="font-medium">Privacy &amp; Security</span> {'→'} <span className="font-medium">Location Services</span></li>
        <li>2. Make sure <span className="font-medium">Location Services</span> is <span className="font-medium">On</span></li>
        <li>3. Scroll to <span className="font-medium">Safari Websites</span> {'→'} <span className="font-medium">While Using</span></li>
      </ol>
    );

    const androidSection = (
      <ol className="text-xs text-muted-foreground space-y-1 mt-1">
        <li>1. Open phone <span className="font-medium">Settings</span> {'→'} <span className="font-medium">Apps</span> {'→'} <span className="font-medium">Chrome</span></li>
        <li>2. Tap <span className="font-medium">Permissions</span> {'→'} <span className="font-medium">{kind}</span></li>
        <li>3. Select <span className="font-medium">Allow</span></li>
      </ol>
    );

    const macSection = kind === 'Camera' ? (
      <ol className="text-xs text-muted-foreground space-y-1 mt-1">
        <li>1. Open <span className="font-medium">System Settings</span> {'→'} <span className="font-medium">Privacy &amp; Security</span> {'→'} <span className="font-medium">Camera</span></li>
        <li>2. Turn on <span className="font-medium">Google Chrome</span></li>
        <li>3. <span className="font-medium">Quit and reopen Chrome</span> (required for the change to take effect)</li>
      </ol>
    ) : (
      <ol className="text-xs text-muted-foreground space-y-1 mt-1">
        <li>1. Open <span className="font-medium">System Settings</span> {'→'} <span className="font-medium">Privacy &amp; Security</span> {'→'} <span className="font-medium">Location Services</span></li>
        <li>2. Turn on <span className="font-medium">Location Services</span> and enable <span className="font-medium">Google Chrome</span></li>
      </ol>
    );

    return (
      <div className="space-y-2">
        <div className="flex items-start gap-3 p-3 bg-muted/50 rounded-lg">
          <Smartphone className="h-5 w-5 text-muted-foreground mt-0.5 shrink-0" />
          <div>
            <p className="font-medium text-sm">iPhone &middot; Settings app</p>
            {iosSection}
          </div>
        </div>
        <div className="flex items-start gap-3 p-3 bg-muted/50 rounded-lg">
          <Settings className="h-5 w-5 text-muted-foreground mt-0.5 shrink-0" />
          <div>
            <p className="font-medium text-sm">Android &middot; Settings app</p>
            {androidSection}
          </div>
        </div>
        <div className="flex items-start gap-3 p-3 bg-muted/50 rounded-lg">
          <Settings className="h-5 w-5 text-muted-foreground mt-0.5 shrink-0" />
          <div>
            <p className="font-medium text-sm">Mac &middot; System Settings</p>
            {macSection}
          </div>
        </div>
      </div>
    );
  };

  const renderRecoveryHelp = (
    kind: 'Camera' | 'Location',
    source: CameraBlockSource = 'unknown',
  ) => {
    // Server-side Permissions-Policy block — no client fix possible.
    if (source === 'policy') {
      return (
        <Alert variant="destructive" className="mt-3">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            This site is currently configured to disallow {kind.toLowerCase()} access. This isn&rsquo;t something you can fix on your device — please contact support.
          </AlertDescription>
        </Alert>
      );
    }

    // Camera with detected source: lead with the layer that's most likely the issue.
    if (kind === 'Camera' && (source === 'os' || source === 'browser')) {
      const primaryLabel = source === 'os'
        ? 'It looks like your phone or computer Settings is the issue, not your browser.'
        : 'It looks like your browser settings is the issue.';
      const primary = source === 'os' ? renderOsSteps(kind) : renderBrowserSteps(kind);
      const secondary = source === 'os' ? renderBrowserSteps(kind) : renderOsSteps(kind);
      const secondaryLabel = source === 'os'
        ? 'Still not working? Try fixing in your browser instead.'
        : 'Still not working? Try fixing in phone or computer Settings instead.';

      return (
        <div className="mt-3 space-y-3 text-left">
          <p className="text-xs italic text-muted-foreground">{primaryLabel}</p>
          {primary}
          <details className="text-xs">
            <summary className="cursor-pointer text-muted-foreground underline">
              {secondaryLabel}
            </summary>
            <div className="mt-2">{secondary}</div>
          </details>
        </div>
      );
    }

    // Location, or camera with unknown source — show both layers, browser first.
    return (
      <div className="mt-3 space-y-4 text-left">
        <div>
          <p className="text-xs font-medium mb-2">Try first &mdash; fix in your browser:</p>
          {renderBrowserSteps(kind)}
        </div>
        <div>
          <p className="text-xs font-medium mb-2">If that doesn&rsquo;t work &mdash; fix in phone or computer Settings:</p>
          {renderOsSteps(kind)}
        </div>
      </div>
    );
  };

  type PermRowConfig = {
    kind: 'Camera' | 'Location';
    icon: typeof Camera;
    state: PermState;
    isRequesting: boolean;
    onRequest: () => void;
    showHelp: boolean;
    onToggleHelp: () => void;
    description: string;
    blockSource?: CameraBlockSource;
  };

  const renderPermissionRow = (cfg: PermRowConfig) => {
    const Icon = cfg.icon;
    const isGranted = cfg.state === 'granted';
    const isDenied = cfg.state === 'denied';
    const isUnsupported = cfg.state === 'unsupported';

    const cardTone = isGranted
      ? 'border-green-200 bg-green-50'
      : isDenied || isUnsupported
      ? 'border-red-200 bg-red-50'
      : 'border-border bg-muted/30';

    return (
      <Card className={`${cardTone} rounded-2xl transition-colors`}>
        <CardContent className="p-5 space-y-3">
          <div className="flex items-start gap-3">
            <div
              className={`shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${
                isGranted ? 'bg-green-100 text-green-700' : isDenied || isUnsupported ? 'bg-red-100 text-red-700' : 'bg-muted text-foreground'
              }`}
            >
              <Icon className="h-5 w-5" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-2">
                <h3 className="font-semibold text-base">{cfg.kind}</h3>
                {isGranted ? (
                  <Badge className="bg-green-100 text-green-800 hover:bg-green-100">
                    <CheckCircle className="h-3.5 w-3.5 mr-1" />
                    Ready
                  </Badge>
                ) : isDenied ? (
                  <Badge variant="destructive">
                    <AlertCircle className="h-3.5 w-3.5 mr-1" />
                    Blocked
                  </Badge>
                ) : isUnsupported ? (
                  <Badge variant="destructive">Unavailable</Badge>
                ) : (
                  <Badge variant="secondary" className="bg-amber-100 text-amber-800 hover:bg-amber-100">
                    Needs access
                  </Badge>
                )}
              </div>
              <p className="text-sm text-muted-foreground mt-1">{cfg.description}</p>
            </div>
          </div>

          {!isGranted && !isUnsupported && (
            <div className="flex flex-col sm:flex-row gap-2">
              {!isDenied && (
                <Button
                  onClick={cfg.onRequest}
                  disabled={cfg.isRequesting || !isSecureContextOk()}
                  className="h-11 flex-1"
                >
                  {cfg.isRequesting ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Requesting...
                    </>
                  ) : (
                    <>Allow {cfg.kind}</>
                  )}
                </Button>
              )}
              {isDenied && (
                <>
                  <Button variant="outline" onClick={cfg.onToggleHelp} className="h-11 flex-1">
                    {cfg.showHelp ? 'Hide instructions' : 'Show me how to fix this'}
                  </Button>
                  <Button onClick={cfg.onRequest} disabled={cfg.isRequesting} className="h-11 flex-1">
                    {cfg.isRequesting ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <RefreshCw className="h-4 w-4 mr-2" />
                    )}
                    Try Again
                  </Button>
                </>
              )}
            </div>
          )}

          {isUnsupported && (
            <p className="text-xs text-red-700">
              Your device or browser doesn&rsquo;t support {cfg.kind.toLowerCase()} access. Please try a different device, or contact support.
            </p>
          )}

          {isDenied && cfg.showHelp && renderRecoveryHelp(cfg.kind, cfg.blockSource)}
        </CardContent>
      </Card>
    );
  };

  const renderPreCheckStep = () => {
    const secure = isSecureContextOk();
    const canContinue = cameraPerm === 'granted' && locationPerm === 'granted';

    return (
      <div className="space-y-5">
        {!secure && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              This page must be opened over HTTPS for camera and location to work. Please open the secure URL on your device.
            </AlertDescription>
          </Alert>
        )}

        {renderPermissionRow({
          kind: 'Camera',
          icon: Camera,
          state: cameraPerm,
          isRequesting: isRequestingCamera,
          onRequest: handleAllowCamera,
          showHelp: showCameraHelp,
          onToggleHelp: () => setShowCameraHelp(s => !s),
          description: 'Used to scan the medallion QR code and capture the installation photo.',
          blockSource: cameraBlockSource,
        })}

        {renderPermissionRow({
          kind: 'Location',
          icon: MapPin,
          state: locationPerm,
          isRequesting: isRequestingLocation,
          onRequest: handleAllowLocation,
          showHelp: showLocationHelp,
          onToggleHelp: () => setShowLocationHelp(s => !s),
          description: 'Records the exact grave location so we can deliver flowers and care services to the right spot.',
        })}

        <div className="pt-2">
          <Button
            onClick={() => setStep('scan')}
            disabled={!canContinue}
            className="w-full h-12 rounded-full"
            size="lg"
          >
            Continue to Install
            <ArrowRight className="h-4 w-4 ml-2" />
          </Button>
          {!canContinue && (
            <p className="text-xs text-center text-muted-foreground mt-3">
              Both camera and location are required before installing.
            </p>
          )}
        </div>
      </div>
    );
  };

  // =========================================================================
  // Render: Step 1 — Scan Medallion
  // =========================================================================

  const renderScanStep = () => (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ScanLine className="h-5 w-5" />
            Scan Medallion QR Code
          </CardTitle>
          <CardDescription>
            Point your camera at the QR code on the medallion
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Native BarcodeDetector path: our own <video>, scanner runs via rAF */}
          {isScanning && scannerKind === 'native' && (
            <div className="relative w-full aspect-square max-w-sm mx-auto rounded-lg overflow-hidden bg-muted">
              <video
                ref={nativeVideoRef}
                autoPlay
                playsInline
                muted
                className="absolute inset-0 w-full h-full object-cover"
              />
            </div>
          )}

          {/* html5-qrcode fallback path: library writes its own <video> into this div */}
          <div
            id="qr-reader-install"
            className={`w-full aspect-square max-w-sm mx-auto rounded-lg overflow-hidden bg-muted ${
              isScanning && scannerKind === 'fallback' ? '' : 'hidden'
            }`}
            style={{ minHeight: isScanning && scannerKind === 'fallback' ? '300px' : '0' }}
          />

          {!isScanning && (
            <div className="flex flex-col items-center justify-center aspect-square max-w-sm mx-auto rounded-lg border-2 border-dashed border-border bg-muted/50">
              <ScanLine className="h-12 w-12 text-muted-foreground mb-4" />
              <Button onClick={startScanner} disabled={isLoading} size="lg" className="h-12">
                <Camera className="h-5 w-5 mr-2" />
                Scan Medallion
              </Button>
            </div>
          )}

          {isScanning && (
            <div className="flex justify-center">
              <Button variant="outline" onClick={stopScanner} className="h-12">
                Stop Camera
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            Or Enter Code Manually
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleManualCodeSubmit} className="flex gap-2">
            <Input
              placeholder="Enter 6-character code (e.g. ABC123)"
              value={manualCode}
              onChange={(e) => setManualCode(e.target.value)}
              className="flex-1 h-12 text-base uppercase"
              maxLength={10}
            />
            <Button type="submit" disabled={!manualCode.trim() || isLoading} className="h-12 px-6">
              {isLoading ? <Loader2 className="h-5 w-5 animate-spin" /> : <ArrowRight className="h-5 w-5" />}
            </Button>
          </form>
        </CardContent>
      </Card>

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
    </div>
  );

  // =========================================================================
  // Render: Step 2 — Select Memorial
  // =========================================================================

  const renderSelectStep = () => {
    const filtered = memorials.filter(m => {
      if (!memorialSearch.trim()) return true;
      const search = memorialSearch.toLowerCase();
      const nameMatch = buildMemorialDisplayName(m).toLowerCase().includes(search);
      const cemeteryMatch = (m.intended_cemetery_name || '').toLowerCase().includes(search);
      return nameMatch || cemeteryMatch;
    });

    return (
      <div className="space-y-4">
        {/* Medallion badge */}
        <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
          <div className="flex items-center gap-2">
            <ScanLine className="h-4 w-4 text-primary" />
            <span className="text-sm font-medium">Medallion: {medallionShortCode}</span>
          </div>
          <Badge variant="secondary" className="bg-green-100 text-green-800">Scanned</Badge>
        </div>

        {/* Sticky search bar */}
        <div className="sticky top-0 z-10 bg-background pb-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search by name or cemetery..."
              value={memorialSearch}
              onChange={(e) => setMemorialSearch(e.target.value)}
              className="pl-9 h-12 text-base"
            />
          </div>
        </div>

        {/* Memorial cards */}
        {isLoadingMemorials ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : filtered.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center space-y-3">
              <p className="text-muted-foreground">
                {memorialSearch ? 'No memorials match your search' : 'No memorials waiting for installation.'}
              </p>
              {!memorialSearch && (
                <Button variant="link" onClick={() => navigate('/portal/memorials')}>
                  Create one first
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className={`space-y-3 ${selectedMemorial ? 'pb-28' : ''}`}>
            {filtered.map(memorial => (
              <Card
                key={memorial.id}
                className={`cursor-pointer transition-all active:scale-[0.98] ${
                  selectedMemorial?.id === memorial.id
                    ? 'ring-2 ring-primary bg-primary/5'
                    : 'hover:bg-muted/30'
                }`}
                onClick={() => setSelectedMemorial(memorial)}
              >
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-lg truncate">
                        {memorial.first_name} {memorial.last_name || ''}
                      </h3>
                      {memorial.intended_cemetery_name && (
                        <p className="text-sm text-muted-foreground flex items-center gap-1 mt-1">
                          <MapPin className="h-3.5 w-3.5 shrink-0" />
                          <span className="truncate">{memorial.intended_cemetery_name}</span>
                        </p>
                      )}
                      <p className="text-xs text-muted-foreground mt-1">
                        Created {new Date(memorial.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    {selectedMemorial?.id === memorial.id && (
                      <div className="ml-3 shrink-0">
                        <CheckCircle className="h-6 w-6 text-primary" />
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Inline nav — only shown before a memorial is selected. Once
            a memorial is picked, the sticky bottom action bar (below) takes
            over so installers don't have to scroll past a long list. */}
        {!selectedMemorial && (
          <div className="flex gap-3 pt-2">
            <Button variant="outline" onClick={handleReset} className="flex-1 h-12">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
            <Button
              onClick={handleContinueToPhoto}
              disabled
              className="flex-1 h-12"
            >
              Continue
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </div>
        )}

        {/* Sticky bottom action bar — only after selection. Breaks out of the
            parent main element's horizontal padding on mobile so it spans
            edge-to-edge, then re-pads its own content. Honors iOS safe-area
            so the buttons sit above the home indicator. */}
        {selectedMemorial && (
          <div
            className="sticky bottom-0 z-20 -mx-4 md:-mx-6 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80 border-t border-border px-4 md:px-6 pt-3 shadow-[0_-4px_16px_rgba(61,53,48,0.08)] animate-in slide-in-from-bottom-2 fade-in duration-300"
            style={{ paddingBottom: 'max(0.75rem, calc(env(safe-area-inset-bottom) + 0.5rem))' }}
          >
            <div className="flex gap-3 max-w-lg mx-auto">
              <Button variant="outline" onClick={handleReset} className="flex-1 h-12">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </Button>
              <Button
                onClick={handleContinueToPhoto}
                className="flex-1 h-12"
              >
                Continue
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </div>
          </div>
        )}
      </div>
    );
  };

  // =========================================================================
  // Render: Step 3 — Confirm & Capture
  // =========================================================================

  const renderConfirmStep = () => {
    const hasGoodAccuracy = bestLocation && bestLocation.accuracy <= ACCURACY_THRESHOLD_GOOD;
    const hasAcceptedOverride = !isWatchingLocation && geolocation && !hasGoodAccuracy;
    const canInstall = !!capturedPhoto && !isLoading && (hasGoodAccuracy || hasAcceptedOverride);

    return (
      <div className="space-y-4">
        {/* Summary */}
        <Card>
          <CardContent className="p-4 space-y-2">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-lg">
                  {selectedMemorial?.first_name} {selectedMemorial?.last_name || ''}
                </h3>
                {selectedMemorial?.intended_cemetery_name && (
                  <p className="text-sm text-muted-foreground flex items-center gap-1">
                    <MapPin className="h-3.5 w-3.5" />
                    {selectedMemorial.intended_cemetery_name}
                  </p>
                )}
              </div>
              <Badge className="bg-primary/10 text-primary shrink-0">
                {medallionShortCode}
              </Badge>
            </div>
          </CardContent>
        </Card>

        {/* Camera / Photo */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Camera className="h-5 w-5" />
              {capturedPhoto ? 'Review Photo' : 'Take Installation Photo'}
            </CardTitle>
            <CardDescription>
              {capturedPhoto
                ? 'Review your photo before completing installation'
                : 'Position the headstone in frame and take a photo'}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Camera / Preview */}
            <div className="relative aspect-[4/3] max-w-md mx-auto bg-muted rounded-lg overflow-hidden">
              {!capturedPhoto && isCameraActive && (
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  muted
                  onLoadedMetadata={(e) => (e.target as HTMLVideoElement).play()}
                  className="w-full h-full object-cover"
                />
              )}
              {capturedPhoto && (
                <img src={capturedPhoto} alt="Captured" className="w-full h-full object-cover" />
              )}
              {!capturedPhoto && !isCameraActive && (
                <div className="flex items-center justify-center h-full">
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
              )}
            </div>
            <canvas ref={canvasRef} className="hidden" />

            {/* Location status */}
            <div className="p-3 rounded-lg border bg-muted/30">
              {isGettingLocation && !bestLocation ? (
                <div className="flex items-center justify-center gap-2 text-sm">
                  <Loader2 className="h-4 w-4 animate-spin text-primary" />
                  <span className="text-muted-foreground">Getting GPS signal...</span>
                </div>
              ) : bestLocation ? (
                <div className="space-y-2">
                  <div className="flex items-center justify-center gap-2 text-sm">
                    {bestLocation.accuracy <= ACCURACY_THRESHOLD_GOOD ? (
                      <div className="flex items-center gap-1.5 text-green-600">
                        <MapPin className="h-4 w-4" />
                        <span className="font-medium">Location locked!</span>
                      </div>
                    ) : bestLocation.accuracy <= 30 ? (
                      <div className="flex items-center gap-1.5 text-blue-600">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        <span className="font-medium">Getting precise location...</span>
                      </div>
                    ) : (
                      <div className="flex items-center gap-1.5 text-yellow-600">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        <span className="font-medium">Improving accuracy...</span>
                      </div>
                    )}
                    <span className="text-muted-foreground">
                      ({'\u00B1'}{bestLocation.accuracy.toFixed(0)}m)
                    </span>
                  </div>

                  <p className="text-xs text-center text-muted-foreground">
                    {bestLocation.latitude.toFixed(6)}, {bestLocation.longitude.toFixed(6)}
                  </p>

                  {bestLocation.accuracy <= ACCURACY_THRESHOLD_GOOD && (
                    <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-center space-y-1">
                      <div className="flex items-center justify-center gap-2">
                        <CheckCircle className="h-5 w-5 text-green-600" />
                        <span className="text-sm font-medium text-green-800">
                          Ready — take a photo!
                        </span>
                      </div>
                    </div>
                  )}

                  {bestLocation.accuracy > ACCURACY_THRESHOLD_GOOD && !showAccuracyOverride && (
                    <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-center space-y-2">
                      <div className="flex items-center justify-center gap-2">
                        <Loader2 className="h-4 w-4 animate-spin text-blue-600" />
                        <span className="text-sm font-medium text-blue-800">
                          Waiting for precise GPS...
                        </span>
                      </div>
                      <div className="w-full bg-blue-200 rounded-full h-2">
                        <div
                          className="bg-blue-600 h-2 rounded-full transition-all duration-500"
                          style={{ width: `${Math.min(100, (ACCURACY_THRESHOLD_GOOD / bestLocation.accuracy) * 100)}%` }}
                        />
                      </div>
                    </div>
                  )}

                  {showAccuracyOverride && bestLocation.accuracy > ACCURACY_THRESHOLD_GOOD && (
                    <div className="p-2 bg-yellow-50 border border-yellow-200 rounded-lg text-center">
                      <p className="text-xs text-yellow-800 mb-2">
                        GPS signal limited. Best available: {'\u00B1'}{bestLocation.accuracy.toFixed(0)}m
                      </p>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={acceptCurrentAccuracy}
                        className="h-8 text-xs"
                      >
                        Continue with {'\u00B1'}{bestLocation.accuracy.toFixed(0)}m
                      </Button>
                    </div>
                  )}
                </div>
              ) : (
                <div className="space-y-2">
                  <div className="flex items-center justify-center gap-2 text-sm text-destructive">
                    <AlertCircle className="h-4 w-4" />
                    <span className="font-medium">Location required</span>
                  </div>
                  {geoError && (
                    <p className="text-xs text-center text-muted-foreground">{geoError}</p>
                  )}
                  <div className="flex justify-center">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => locationPermissionDenied ? setShowLocationDialog(true) : startLocationWatch()}
                      className="h-10"
                    >
                      <MapPin className="h-3.5 w-3.5 mr-1.5" />
                      {locationPermissionDenied ? 'Enable Location' : 'Retry Location'}
                    </Button>
                  </div>
                </div>
              )}
            </div>

            {/* Actions */}
            {!capturedPhoto ? (
              <div className="flex justify-center">
                <Button
                  onClick={capturePhoto}
                  disabled={!isCameraActive || !bestLocation || bestLocation.accuracy > ACCURACY_THRESHOLD_GOOD}
                  size="lg"
                  className="rounded-full h-16 w-16"
                >
                  <Camera className="h-6 w-6" />
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="flex gap-3">
                  <Button variant="outline" onClick={retakePhoto} className="flex-1 h-12">
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Retake
                  </Button>
                  <Button
                    onClick={handleInstallation}
                    disabled={!canInstall}
                    className="flex-1 h-12"
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Installing...
                      </>
                    ) : (
                      <>
                        <Check className="h-4 w-4 mr-2" />
                        Complete Installation
                      </>
                    )}
                  </Button>
                </div>
                {!geolocation && capturedPhoto && (
                  <p className="text-xs text-center text-destructive">
                    Location is required. GPS coordinates enable accurate service delivery.
                  </p>
                )}
              </div>
            )}

            {error && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
          </CardContent>
        </Card>

        <Button variant="outline" onClick={() => { stopCamera(); stopLocationWatch(); setStep('select'); setCapturedPhoto(null); setCapturedBlob(null); setBestLocation(null); }} className="w-full h-12">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Memorial Selection
        </Button>
      </div>
    );
  };

  // =========================================================================
  // Render: Step 4 — Success
  // =========================================================================

  const renderSuccessStep = () => (
    <div className="space-y-6">
      <Card>
        <CardContent className="pt-6">
          <div className="text-center space-y-4">
            <div className="mx-auto w-16 h-16 rounded-full bg-green-100 flex items-center justify-center">
              <Check className="h-8 w-8 text-green-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-foreground">
                Memorial Installed!
              </h2>
              <p className="text-muted-foreground mt-1">
                The medallion has been linked and the installation recorded.
              </p>
            </div>

            <div className="p-4 bg-muted/50 rounded-lg max-w-xs mx-auto space-y-2">
              <p className="font-medium text-lg">
                {selectedMemorial?.first_name} {selectedMemorial?.last_name || ''}
              </p>
              {selectedMemorial?.intended_cemetery_name && (
                <p className="text-sm text-muted-foreground flex items-center justify-center gap-1">
                  <MapPin className="h-3.5 w-3.5" />
                  {selectedMemorial.intended_cemetery_name}
                </p>
              )}
              <Badge className="bg-green-100 text-green-800">
                Medallion: {medallionShortCode}
              </Badge>
            </div>

            {geolocation && (
              <p className="text-xs text-muted-foreground flex items-center justify-center gap-1">
                <MapPin className="h-3 w-3" />
                {geolocation.latitude.toFixed(6)}, {geolocation.longitude.toFixed(6)}
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      <div className="flex gap-3">
        <Button variant="outline" onClick={handleReset} className="flex-1 h-12">
          <ScanLine className="h-4 w-4 mr-2" />
          Scan Another
        </Button>
        <Button onClick={() => navigate('/portal/memorials')} className="flex-1 h-12">
          View Memorials
          <ArrowRight className="h-4 w-4 ml-2" />
        </Button>
      </div>
    </div>
  );

  // =========================================================================
  // Location Permission Dialog
  // =========================================================================

  const LocationPermissionDialog = () => (
    <Dialog open={showLocationDialog} onOpenChange={setShowLocationDialog}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MapPin className="h-5 w-5 text-primary" />
            Location Access Required
          </DialogTitle>
          <DialogDescription>
            Installation photos require GPS coordinates to enable accurate grave-level service delivery.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-3">
            <div className="flex items-start gap-3 p-3 bg-muted/50 rounded-lg">
              <Smartphone className="h-5 w-5 text-muted-foreground mt-0.5 shrink-0" />
              <div>
                <p className="font-medium text-sm">iPhone</p>
                <ol className="text-xs text-muted-foreground space-y-1 mt-1">
                  <li>1. Open <span className="font-medium">Settings</span> {'\u2192'} <span className="font-medium">Safari</span> (or your browser)</li>
                  <li>2. Tap <span className="font-medium">Location</span></li>
                  <li>3. Select <span className="font-medium">Allow</span> or <span className="font-medium">Ask</span></li>
                  <li>4. Return here and tap "Try Again"</li>
                </ol>
              </div>
            </div>

            <div className="flex items-start gap-3 p-3 bg-muted/50 rounded-lg">
              <Settings className="h-5 w-5 text-muted-foreground mt-0.5 shrink-0" />
              <div>
                <p className="font-medium text-sm">Android</p>
                <ol className="text-xs text-muted-foreground space-y-1 mt-1">
                  <li>1. Tap the <span className="font-medium">lock icon</span> in the address bar</li>
                  <li>2. Tap <span className="font-medium">Site settings</span> {'\u2192'} <span className="font-medium">Location</span></li>
                  <li>3. Select <span className="font-medium">Allow</span></li>
                  <li>4. Return here and tap "Try Again"</li>
                </ol>
              </div>
            </div>
          </div>

          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => setShowLocationDialog(false)}
              className="flex-1 h-12"
            >
              Cancel
            </Button>
            <Button onClick={handleRetryLocation} className="flex-1 h-12">
              <RefreshCw className="h-4 w-4 mr-2" />
              Try Again
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );

  // =========================================================================
  // Main Render
  // =========================================================================

  if (isReadOnly) {
    return (
      <div className="max-w-lg mx-auto">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-foreground">Install Medallion</h1>
          <p className="text-muted-foreground mt-1">
            Scan a medallion, select a memorial, and capture the installation
          </p>
        </div>
        <Card>
          <CardContent className="py-12 text-center">
            <ScanLine className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">
              Installation is disabled while your account is deactivated.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const steps: { key: Step; label: string }[] = [
    { key: 'scan', label: 'Scan' },
    { key: 'select', label: 'Select' },
    { key: 'confirm', label: 'Install' },
    { key: 'success', label: 'Done' },
  ];

  const currentStepIndex = steps.findIndex(s => s.key === step);

  const isPreCheck = step === 'pre-check';

  return (
    <div className="max-w-lg mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-foreground">
          {isPreCheck ? 'Before we begin' : 'Install Medallion'}
        </h1>
        <p className="text-muted-foreground mt-1">
          {isPreCheck
            ? 'Two quick permissions and you’re ready to install.'
            : 'Scan a medallion, select a memorial, and capture the installation'}
        </p>
      </div>

      {/* Step Indicator (hidden during pre-check so the gate stays uncluttered) */}
      {!isPreCheck && (
        <div className="flex items-center justify-center gap-2 mb-8">
          {steps.map((s, i) => (
            <div key={s.key} className="flex items-center">
              <div className="flex flex-col items-center">
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors ${
                    i === currentStepIndex
                      ? 'bg-primary text-primary-foreground'
                      : i < currentStepIndex
                      ? 'bg-primary/20 text-primary'
                      : 'bg-muted text-muted-foreground'
                  }`}
                >
                  {i < currentStepIndex ? <Check className="h-4 w-4" /> : i + 1}
                </div>
                <span className={`text-xs mt-1 ${i === currentStepIndex ? 'text-primary font-medium' : 'text-muted-foreground'}`}>
                  {s.label}
                </span>
              </div>
              {i < steps.length - 1 && (
                <div className={`w-8 h-0.5 mb-5 ${
                  i < currentStepIndex ? 'bg-primary/20' : 'bg-muted'
                }`} />
              )}
            </div>
          ))}
        </div>
      )}

      {step === 'pre-check' && renderPreCheckStep()}
      {step === 'scan' && renderScanStep()}
      {step === 'select' && renderSelectStep()}
      {step === 'confirm' && renderConfirmStep()}
      {step === 'success' && renderSuccessStep()}

      <LocationPermissionDialog />
    </div>
  );
};

export default PortalUploadPhoto;
